import { LANDSCAPE_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/landscapeBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Tempest — Stormbreak Cliffs: a raging coastal storm. A dark roiling storm-cloud sky (deep
 *  blue-gray gradient + heavy fast-drifting cloud bands), cold spindrift/sea-spray motes, faint
 *  storm-light shafts breaking through, a swelling cold horizon glow, a churning storm-energy bed,
 *  DRIVING wind-blown rain (fall, heavy + fast + strong windX), sharp white-blue LIGHTNING flashes,
 *  wind-whipped sea-cliff silhouettes, driven sea-mist, and a cold steel-blue grade. Cold, violent,
 *  dramatic — storm-blue + slate-gray (contrast kept; no pale wash). */
export const TEMPEST: AtmospherePreset = {
  id: 'tempest',
  layers: [
    // Storm sky — deep slate-blue zenith → murky slate horizon (kept dark + saturated so the
    // sea-cliff silhouettes below read against it, never washed pale).
    { kind: 'skyGradient', from: 0x14202e, to: 0x33414f },
    // Heavy roiling storm clouds racing across the upper sky — LIT cool-slate tints (glow is
    // additive, so dark/near-sky tints render invisible). High count + fast drift = stormy churn.
    { kind: 'cloudBands', count: 7, color: 0x8fa3b5, sizeMin: 420, sizeMax: 760, alphaMin: 0.08, alphaMax: 0.18, driftPxPerSec: 14, bandYMin: 0.04, bandYMax: 0.40 },
    // Cold spindrift / blown sea-spray motes — pale icy blue, small + fast-wandering on the gale.
    { kind: 'fireflies', count: 20, colors: [P.ice, P.frostCore, 0xcfe6f5], sizeMin: 3, sizeMax: 9, driftPx: 50, periodMinMs: 3000, periodMaxMs: 7000, alphaMin: 0.15, alphaMax: 0.55 },
    // Faint cold storm-light shafts breaking through the cloud (wind-tilted, dim — not warm sun).
    { kind: 'godRays', count: 2, color: P.frostCore, widthMin: 50, widthMax: 120, height: 520, tiltDeg: 14, periodMinMs: 5000, periodMaxMs: 10000, alphaMin: 0.06, alphaMax: 0.22 },
    // Cold horizon glow (sea-line) + a dim storm-light bloom up top — swelling fast (storm energy).
    breathingGlowBlobs(
      [
        { color: 0x4a6276, atX: 0.5, atY: 0.95, size: 560, alpha: 0.12 },
        { color: P.frostCore, atX: 0.5, atY: 0.08, size: 420, alpha: 0.06 },
      ],
      { periodMs: 4000, min: 0.7, max: 1.4 },
    ),
    // Churning cold storm-energy bed shimmering low.
    { kind: 'lightField', count: 13, palette: [P.frostCore, P.ice, 0x5b7c93], sizeMin: 110, sizeMax: 280, alphaMin: 0.04, alphaMax: 0.16, driftRadius: 80, periodMinMs: 5000, periodMaxMs: 10000 },
    // DRIVING RAIN — pale blue-white streaks falling heavy + fast, slammed sideways by a strong
    // wind (high spawn rate, short lifespan, big windX), the squall gusting in waves.
    gustingWeather(
      { mode: 'fall', color: 0xbcd6ea, colorEnd: 0x6f93ad,
        size: 4, frequencyMs: 55, lifespanMs: 1400, speed: 80, gravityY: 40, windX: -90, alphaEnd: 0.5 },
      { periodMs: 5000, min: 0.7, max: 1.6, phase: 0.2 },
    ),
    // Sharp LIGHTNING strikes high in the storm sky — the real `lightning` kind (was a
    // flicker stand-in): 3 independent anchors, each a broad white-blue sky flash that
    // back-lights the sea-cliffs + a thin bolt streak, on randomised intervals (mostly
    // dark, sharp double-flash strikes). Reduce-motion disables the strobe.
    { kind: 'lightning', color: 0xeaf4ff, bolts: 3, intervalMinMs: 3500, intervalMaxMs: 8000, flashAlpha: 0.5, boltAlpha: 0.85 },
    // Wind-whipped sea-cliff silhouettes (PX) read against the slate storm horizon — far rolling
    // ridge → mid jagged crags → near sea-stacks. Dark slate (near = darkest/most-opaque); the far
    // ridge is a touch lighter (storm haze) + sways hard in the gale.
    ...multiPlaneParallax([
      { svg: LANDSCAPE_BACKDROPS.tempest.far, shape: 'ridge', color: 0x2a3744, speed: 0.04, height: 440, alpha: 0.72, detail: 0.8, sway: { ampPx: 16, periodMs: 20000 } },
      { svg: LANDSCAPE_BACKDROPS.tempest.mid, shape: 'peaks', color: 0x1d2832, speed: 0.10, height: 320, alpha: 0.85, detail: 1.1, sway: { ampPx: 9, periodMs: 16000 } },
      { svg: LANDSCAPE_BACKDROPS.tempest.near, shape: 'columns', color: 0x10171e, speed: 0.20, height: 190, alpha: 0.94, detail: 1 },
    ]),
    // Driven sea-mist drifting through (cold slate haze).
    { kind: 'fog', color: 0x5a7488, bands: 5, alphaMin: 0.05, alphaMax: 0.14, driftPx: 60, periodMinMs: 7000, periodMaxMs: 13000 },
    ...biomeCloser({
      vignette: { color: 0x0c141d, edgeAlpha: 0.38, washColor: 0x3d6f96, washAlpha: 0.06 },
      // Cold steel-blue storm grade — a 'normal' veil (tints cold + keeps the scene DARK + saturated,
      // not the 'screen' lift that would wash the storm pale).
      grade: { top: 0x6f9fc4, bottom: 0x2a3e52, strength: 0.11, blend: 'normal' },
    }),
  ],
};
